import type {
  ActForPerson,
  ActForRole,
  ApprovalDetail,
  DocumentView,
  Me,
  OthersRow,
} from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useRef, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { actedForAwardQuery, actedForFileHref } from '../../api/actingFor';
import { approvalsQuery } from '../../api/ops';
import { FileIcon, Pair, ScanWords, shared } from '../../acting-for/parts';
import { doneByLine, firstName, sizeWords, withNoteAndFiles } from '../../acting-for/words';
import { Hero, PageBody, TwoColumns, type HeroStat } from '../../shell';
import {
  ButtonLink,
  Card,
  formatCount,
  formatMoney,
  formatTimestamp,
  Icon,
  scoped,
  type StatusTone,
} from '../../ui';
import { amountWords, KIND_LABEL } from '../labels';
import { Notice } from '../shared';
import { LeadWaitingList, useLeadWaiting } from './LeadWaiting';
import { listCard, names } from './presenter';
import o from './Others.module.css';
import s from '../Ops.module.css';

/** A top-up the action added, and where it left its pool. */
export interface ActedTopUp {
  pool: string;
  cashCents: number;
  options: number;
  /** The pool's cash left after the award and the top-up. */
  afterCents: number;
}

/** What was decided for the person: each has its own words after it (Approved board). */
export type ActedKind = 'approve' | 'decline' | 'reject' | 'return' | 'record';

/** What a Stock Admin just did for an Executive or a Sector Lead on an award (Approved board). */
export interface ActedDone {
  awardId: string;
  /** Whose work it was: an Executive's, or the award's Sector Lead's. */
  role: ActForRole;
  kind: ActedKind;
  person: ActForPerson;
  note: string | null;
  files: DocumentView[];
  /** When the server recorded it (R125); null when its answer didn't say. */
  at: string | null;
  /** The top-ups it added (a zero top-up adds nothing). */
  topUps: ActedTopUp[];
  /** The pool the hero shows: its name and cash left after this. */
  pool: { name: string; leftCents: number } | null;
  /** Where an approved or recorded award went next; null for one sent back or ended. */
  goesTo: 'stock_admin' | 'executives' | null;
  /** The note or reason to whoever entered it: a decline's, a rejection's or a return's. */
  toCreator: string | null;
}

/** "Approved", "Declined", …: the banner's first word and the verb in "hears it was …". */
const DONE: Record<ActedKind, { title: string; past: string }> = {
  approve: { title: 'Approved', past: 'approved' },
  decline: { title: 'Declined', past: 'declined' },
  reject: { title: 'Rejected', past: 'rejected' },
  return: { title: 'Returned', past: 'returned' },
  record: { title: 'Recorded', past: 'recorded' },
};

/** " with a $2,913 top-up to Terrestrial", " with top-ups: $2,913 to Terrestrial and $500 to …". */
function topUpWords(topUps: readonly ActedTopUp[]): string {
  const [only] = topUps;
  if (only === undefined) return '';
  if (topUps.length === 1)
    return ` with a ${amountWords(only.cashCents, only.options)} top-up to ${only.pool}`;
  return ` with top-ups: ${names(topUps.map((t) => `${amountWords(t.cashCents, t.options)} to ${t.pool}`))}`;
}

/** "Terrestrial got a $2,913 top-up and is at $0 for the rest of 2026." */
function poolSentence(t: ActedTopUp, year: number | null): string {
  const rest = year === null ? 'the rest of the year' : `the rest of ${String(year)}`;
  const where =
    t.afterCents === 0
      ? 'is at $0'
      : t.afterCents > 0
        ? `has ${formatMoney(t.afterCents)} left`
        : `is still ${formatMoney(-t.afterCents)} over budget`;
  return `${t.pool} got a ${amountWords(t.cashCents, t.options)} top-up and ${where} for ${rest}.`;
}

/** The words of what was done: the hero's pill, the banner, the Decision row and Next. */
function wordsFor(
  done: ActedDone,
  detail: ApprovalDetail,
): { pill: { label: string; tone: StatusTone }; after: string; decision: string; next: string } {
  const { award } = detail;
  const id = award.displayId ?? 'The award';
  const creator = award.createdBy.name;
  const year = award.fiscalYear ?? null;
  const quoted = done.toCreator === null ? '' : `: “${done.toCreator}”`;
  switch (done.kind) {
    case 'approve':
    case 'record': {
      const onward = done.goesTo === 'executives';
      return {
        pill: onward
          ? { label: 'Waiting on the Executives', tone: 'waiting' }
          : { label: 'With the Stock Admin team', tone: 'info' },
        after: `${onward ? `${id} went on to the Executives.` : `${id} is with the Stock Admin team.`}${done.topUps.map((t) => ` ${poolSentence(t, year)}`).join('')}`,
        decision:
          done.kind === 'record'
            ? `Recorded as approved by ${done.person.name}`
            : `Approved${topUpWords(done.topUps)}`,
        next: onward
          ? 'The Executives decide it. When one asks you to, you can do it for them.'
          : 'The Stock Admin team’s check. Anyone on the team can claim it, you included.',
      };
    }
    case 'decline':
      return {
        pill: { label: `Back with ${creator}`, tone: 'waiting' },
        after: `${id} went back to ${creator} to reduce. No budget changed.`,
        decision: `Declined${done.toCreator === null ? '' : `, with a note to ${creator}${quoted}`}`,
        next: `${creator} can change it and send it again.`,
      };
    case 'return':
      return {
        pill: { label: `Back with ${creator}`, tone: 'waiting' },
        after: `${id} went back to ${creator} to change and send again.`,
        decision: `Returned to ${creator} with a note${quoted}`,
        next: `${creator} can change it and send it again.`,
      };
    case 'reject':
      return {
        pill: { label: 'Turned down', tone: 'problem' },
        after: `${id} won’t be paid.`,
        decision: `Rejected${done.toCreator === null ? '' : `, with a reason for ${creator}${quoted}`}`,
        next: 'Nothing more. It won’t be paid.',
      };
  }
}

/** The Executives' other decisions a Stock Admin may do for them, from the approvals list. */
function useExecutivesWaiting(detail: ApprovalDetail): ApprovalDetail[] {
  const { data: list } = useQuery(approvalsQuery);
  return (list?.items ?? []).filter(
    (d) => d.award.id !== detail.award.id && d.actingFor?.role === 'executive',
  );
}

function StillWaitingOnExecutives({
  still,
  first,
}: {
  still: readonly ApprovalDetail[];
  first: string;
}): ReactElement {
  return (
    <StillCard
      title={`Still waiting on the Executives · ${formatCount(still.length)}`}
      first={first}
    >
      {still.length === 0 ? (
        <p className={s.muted}>Nothing else is waiting on the Executives.</p>
      ) : (
        <ul className={o.stillList}>
          {still.slice(0, 5).map((d) => {
            const card = listCard(d);
            return (
              <li key={card.id}>
                <Link href={`/approvals/${card.id}`} className={s.strongLink}>
                  {card.displayId}
                </Link>
                <span className={s.sub}>
                  {d.kind === null ? '' : `${KIND_LABEL[d.kind]} · `}
                  {card.summary}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </StillCard>
  );
}

function StillWaitingOnLead({
  rows,
  person,
}: {
  rows: readonly OthersRow[];
  person: ActForPerson;
}): ReactElement {
  const first = firstName(person.name);
  return (
    <StillCard title={`Still waiting on ${first} · ${formatCount(rows.length)}`} first={first}>
      <LeadWaitingList rows={rows} first={first} />
    </StillCard>
  );
}

function StillCard({
  title,
  first,
  children,
}: {
  title: string;
  first: string;
  children: ReactElement;
}): ReactElement {
  return (
    <Card
      title={title}
      titleSize="sm"
      compact
      help={{
        shows: 'Other work waiting on them, oldest first.',
        canDo: 'When they ask you to, open the next one and do it for them.',
      }}
    >
      {children}
      <Notice tone="info">
        Doing more for {first}? Open the next one. Files and a note are optional each time.
      </Notice>
      <ButtonLink href="/approvals" variant="ghost" size="sm" block>
        All approvals
      </ButtonLink>
    </Card>
  );
}

/**
 * After a Stock Admin decides an award for an Executive or a Sector Lead (APP-148, Approved
 * board): what was decided, who did it for whom, when, the note and files, what's next, and
 * what's still waiting on them.
 */
export function ActedConfirmation({
  done,
  detail,
  me,
}: {
  done: ActedDone;
  detail: ApprovalDetail;
  me: Me;
}): ReactElement {
  const { award } = detail;
  const id = award.displayId ?? 'The award';
  const creator = award.createdBy.name;
  const first = firstName(done.person.name);
  const actor = me.person.displayName;
  const words = wordsFor(done, detail);
  const lead = useRef<HTMLParagraphElement>(null);
  // The step and the page it opened from are gone: focus starts at what happened.
  useLayoutEffect(() => {
    lead.current?.focus();
  }, []);

  // Read once, here, for the hero and the side card; a lead's work only after deciding for one.
  const executivesWaiting = useExecutivesWaiting(detail);
  const leadWaiting = useLeadWaiting(done.person.id, detail, done.role === 'sector_lead');
  // The record of the action, for each file's download (the files are attached to it now). It's
  // the record holding these very files: their ids are this action's alone (one upload per step).
  const fileIds = new Set(done.files.map((f) => f.id));
  const { data: rows } = useQuery({
    ...actedForAwardQuery(award.id),
    enabled: fileIds.size > 0,
    retry: false,
  });
  const row = (rows ?? []).find((r) => r.files.some((f) => fileIds.has(f.id)));

  const year = award.fiscalYear ?? null;
  const stats: HeroStat[] = [
    ...(done.pool === null
      ? []
      : [
          {
            label: scoped(
              done.pool.name,
              year === null ? 'cash left this year' : `cash left ${String(year)}`,
            ),
            value: formatMoney(done.pool.leftCents),
          },
        ]),
    done.role === 'executive'
      ? { label: 'Executives · still waiting', value: formatCount(executivesWaiting.length) }
      : {
          label: scoped(done.person.name, 'still waiting'),
          value: formatCount(leadWaiting.length),
        },
  ];
  // Recording names the Executive; whoever asked for it hears of it as recording says today.
  const told = `${first} has a notice in the bell and by email${withNoteAndFiles(done.note !== null, done.files.length)}.${done.kind === 'record' ? '' : ` ${creator} hears it was ${DONE[done.kind].past}, as usual.`}`;

  return (
    <>
      <Hero
        title={id}
        pill={words.pill}
        stats={stats}
        back={{ href: '/approvals', label: 'Approvals', name: 'Back to Approvals' }}
        titleTestId="approval-title"
      />
      <PageBody>
        <div className={s.stack}>
          <div className={o.banner} role="status">
            <Icon name="checkCircle" size={20} />
            <div className={o.bannerText}>
              <p>
                <span className={o.bannerLead} ref={lead} tabIndex={-1}>
                  {DONE[done.kind].title} for {done.person.name}.
                </span>{' '}
                {words.after}
              </p>
              <p>{told}</p>
            </div>
          </div>
          <TwoColumns
            sideLabel={
              done.role === 'executive'
                ? 'Still waiting on the Executives'
                : `Still waiting on ${first}`
            }
            main={
              <Card
                title="The decision"
                help={{
                  shows: `What you decided for ${done.role === 'executive' ? 'the Executive' : 'the Sector Lead'}, recorded as your action done for them, with your note and files.`,
                  canDo:
                    'Open the award record for its history. The note and files can be changed there by the Stock Admin team and the System Admins.',
                }}
                aside={
                  <ButtonLink href={`/awards/${award.id}`} variant="ghost" size="sm">
                    Open the award record
                  </ButtonLink>
                }
              >
                <dl className={o.facts}>
                  <dt>Decision</dt>
                  <dd>{words.decision}</dd>
                  <dt>Done by</dt>
                  <dd>
                    <Pair actor={actor} person={done.person.name} />
                    <span>{doneByLine(actor, done.person, done.role)}</span>
                  </dd>
                  {done.at !== null && (
                    <>
                      <dt>When</dt>
                      <dd>{formatTimestamp(done.at)}</dd>
                    </>
                  )}
                  <dt>Note</dt>
                  <dd>
                    {done.note === null ? (
                      <span className={s.muted}>No note added</span>
                    ) : (
                      <p className={o.quote}>{done.note}</p>
                    )}
                  </dd>
                  <dt>Files</dt>
                  <dd>
                    {done.files.length === 0 ? (
                      <span className={s.muted}>No files added</span>
                    ) : (
                      <ul className={shared.files}>
                        {done.files.map((file) => {
                          const kept = row?.files.find((f) => f.id === file.id) ?? file;
                          const opens =
                            row !== undefined &&
                            (kept.scanStatus === 'clean' || kept.scanStatus === 'not_required');
                          return (
                            <li key={file.id} className={shared.file}>
                              <FileIcon contentType={kept.contentType} />
                              <span className={shared.fileText}>
                                <span className={shared.fileName}>{kept.fileName}</span>
                                <span className={shared.fileMeta}>
                                  {sizeWords(kept.sizeBytes)}
                                  <ScanWords status={kept.scanStatus} />
                                </span>
                              </span>
                              {opens && (
                                <ButtonLink
                                  href={`${actedForFileHref(row.id, kept.id)}?disposition=inline`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`View ${kept.fileName}`}
                                >
                                  View
                                </ButtonLink>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </dd>
                  <dt>Next</dt>
                  <dd>{words.next}</dd>
                </dl>
              </Card>
            }
            side={
              done.role === 'executive' ? (
                <StillWaitingOnExecutives still={executivesWaiting} first={first} />
              ) : (
                <StillWaitingOnLead rows={leadWaiting} person={done.person} />
              )
            }
          />
        </div>
      </PageBody>
    </>
  );
}
